'use client'

import EnterView from '@/components/enterView'
import { ModuleName, ModuleTitle } from '@/components/layout/modules'
import Numbers from '@/components/numbers'
import { cn } from '@utils'
import Image from 'next/image'
import ImgMap from '@image/reach/map.webp'
import dynamic from 'next/dynamic'
import { useState } from 'react'

const BarChart = dynamic(() => import('./chart'), {
  ssr: false,
  loading: () => <div className='skeleton h-75'></div>,
})

export default function Stats() {
  return (
    <section className='section mt-7.5 lg:mt-25'>
      <div className='text-center space-y-4'>
        <ModuleName>Stats</ModuleName>
        <ModuleTitle>Fast‑Facts panel</ModuleTitle>
        <p className='text-font-primary/70'>
          Traffic on different platforms.
        </p>
        <strong className='mt-12.5 block italic'>
          - High-intent Asian buyers every month -
        </strong>
      </div>
      <div className='grid grid-cols-2 gap-4 lg:grid-cols-5 lg:gap-x-7.5 mt-7.5'>
        <GridItem number={480} label='Monthly Visitors' unit='K+' big />
        <GridItem number={350} label='Website' />
        <GridItem
          number={95}
          label={
            <>
              App
              <br />
              (iOS & Android)
            </>
          }
        />
        <GridItem number={35} label='Wechat miniprogram' />
      </div>
      <div className='shadow-card mt-12.5 rounded-lg grid grid-cols-1 lg:grid-cols-2'>
        <ChartItem title='Age Breakdown'>
          <BarChart />
        </ChartItem>
        <ChartItem title='We Reach 46% of the local Chinese Adult population in New Zealand each month.'>
          <div className='relative h-75'>
            <Image
              src={ImgMap}
              alt='We reach 46% of the local Chinese adult population in New Zealand each month.'
              unoptimized
              className='w-full h-full object-contain'
            />
            <div className='text-primary font-bold text-6xl absolute left-1/2 top-1/2 -translate-1/2 opacity-0 animate-fade-in'>
              46%
            </div>
          </div>
        </ChartItem>
      </div>
    </section>
  )
}

function GridItem({
  number,
  label,
  unit = 'K',
  big = false,
}: {
  number: number
  label: React.ReactNode
  unit?: string
  big?: boolean
}) {
  const [start, setStart] = useState(false)
  return (
    <div
      className={cn(
        'rounded-lg grid grid-rows-subgrid row-span-2 text-center pt-16.75 pb-12',
        big
          ? 'bg-primary lg:col-start-1 lg:col-end-3'
          : 'border border-secondary/20'
      )}
    >
      <div
        className={cn(
          'text-5xl font-bold',
          { 'opacity-0': !start },
          big ? 'text-white' : 'text-primary'
        )}
      >
        <Numbers value={number} onStart={() => setStart(true)} />
        {unit}
      </div>
      <div
        className={cn(
          'mt-1.75 font-bold',
          big ? 'text-white/70' : 'text-font-primary/70'
        )}
      >
        {label}
      </div>
    </div>
  )
}

function ChartItem({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <div className='relative group grid grid-rows-subgrid row-span-2 p-10 lg:px-20'>
      <strong className='text-center'>{title}</strong>
      <div className='mt-10'>
        <EnterView skeleton={<div className='skeleton h-75'></div>}>
          {children}
        </EnterView>
      </div>
      <i className='group-first:block max-lg:border-t max-lg:w-4/5 max-lg:bottom-0 max-lg:left-1/2 max-lg:-translate-x-1/2 lg:border-r hidden border-dashed border-secondary/30 lg:h-4/5 absolute lg:right-0 lg:top-1/2 lg:-translate-y-1/2'></i>
    </div>
  )
}
